import React from 'react';
import { connect } from 'react-redux';
import { Box, Card, HorizontalScroll, Icon, Image, Tooltip, Typography } from '@drone-js/core';
import { mapStateToProps } from 'component/page/detail/Highlights/ActivitiesHighlights.selectors';
import { HighlightsItemProps, MAX_HIGHLIGHTS_ITEMS, StateProps } from 'component/page/detail/Highlights/ActivitiesHighlights.types';
import { getImageUrl, getPropertyHighlightsImageUrl } from 'core/utils/activityUrlGenerator';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import {
    ActivitiesHighlightsContainerSeenEventActivitiesFieldsV1,
    ActivitiesHighlightsItemSeenEventActivitiesFieldsV1,
    ActivitiesHighlightsTooltipClickEventActivitiesFieldsV1,
    ActivitiesHighlightsTooltipHoverEventActivitiesFieldsV1,
    ActivitiesHighlightsTooltipSeenEventActivitiesFieldsV1,
} from '@agoda/analytics-messages-definition-activities/events.split';
import {
    ActivitiesHighlightsContainerSeenEventActivitiesManifestV1,
    ActivitiesHighlightsItemSeenEventActivitiesManifestV1,
    ActivitiesHighlightsTooltipClickEventActivitiesManifestV1,
    ActivitiesHighlightsTooltipHoverEventActivitiesManifestV1,
    ActivitiesHighlightsTooltipSeenEventActivitiesManifestV1,
} from '@agoda/analytics-messages-definition-activities/manifest/events';
import { InView } from 'react-intersection-observer';

const ActivitiesHighlightsItem: React.FC<HighlightsItemProps> = (props) => {
    const { cms, highlightsItem, index, isMobile } = props;
    const imageUrl = highlightsItem.isPropertyImage ? getPropertyHighlightsImageUrl(highlightsItem.imageName) : getImageUrl(`highlights/${highlightsItem.imageName}`);

    const onHighlightsItemSeen = (inView: boolean) => {
        if (!inView) return;
        trackElementEvent<ActivitiesHighlightsItemSeenEventActivitiesFieldsV1>(ActivitiesHighlightsItemSeenEventActivitiesManifestV1.ids, {
            action_element_value: highlightsItem.code,
            action_element_index: index,
        });
    };

    return (
        // For desktop, manually split the container into MAX_HIGHLIGHTS_ITEMS columns which will leave a space when the actual item is less than MAX_HIGHLIGHTS_ITEMS
        <Box display="flex" width={isMobile ? '100%' : `${100 / MAX_HIGHLIGHTS_ITEMS}%`} justifyContent="center">
            <InView onChange={onHighlightsItemSeen} triggerOnce={true} threshold={0.8}>
                <Box
                    data-element-name="activities-highlights-item"
                    data-testid="activities-highlights-item"
                    data-element-index={index}
                    display="flex"
                    alignItems="center"
                    textAlign="center"
                    flexDirection="column"
                    maxWidth="118px"
                    width="max-content"
                    pe={isMobile ? '16' : undefined}
                >
                    <Image src={imageUrl} alt={cms[highlightsItem.cmsId]} width="32px" height="32px" mb="8" />
                    <Typography variant="caption-default-strong">{cms[highlightsItem.cmsId]}</Typography>
                </Box>
            </InView>
        </Box>
    );
};

ActivitiesHighlightsItem.displayName = 'ActivitiesHighlightsItem';

const ActivitiesHighlights: React.FC<StateProps> = (props) => {
    const { cms, highlightsItems, isLoading, isMobile } = props;
    const highlightsItemsLength = highlightsItems.length;

    if (isLoading || !highlightsItemsLength) {
        return null;
    }

    const onHighlightsSeen = (inView: boolean) => {
        if (!inView) return;
        trackElementEvent<ActivitiesHighlightsContainerSeenEventActivitiesFieldsV1>(ActivitiesHighlightsContainerSeenEventActivitiesManifestV1.ids, {});
        trackElementEvent<ActivitiesHighlightsTooltipSeenEventActivitiesFieldsV1>(ActivitiesHighlightsTooltipSeenEventActivitiesManifestV1.ids, {});
    };

    const onTooltipTouch = () => {
        trackElementEvent<ActivitiesHighlightsTooltipClickEventActivitiesFieldsV1>(ActivitiesHighlightsTooltipClickEventActivitiesManifestV1.ids, {});
    };

    const onTooltipHover = () => {
        trackElementEvent<ActivitiesHighlightsTooltipHoverEventActivitiesFieldsV1>(ActivitiesHighlightsTooltipHoverEventActivitiesManifestV1.ids, {});
    };

    return (
        <InView onChange={onHighlightsSeen} triggerOnce={true} threshold={1}>
            <Card
                data-element-name="activities-highlights-container"
                data-testid="activities-highlights-container"
                p={isMobile ? '16' : '24'}
                pe={isMobile ? '0' : '24'}
                mb="24"
                variant="filled"
            >
                <Box display="flex" mb={isMobile ? '16' : '24'} alignItems="center">
                    <Typography variant="heading-l" me="8">
                        {props.cms[351210]}
                    </Typography>
                    <Tooltip data-element-name="activities-highlights-tooltip" data-testid="activities-highlights-tooltip" content={props.cms[351208]} onTouchEnd={onTooltipTouch}>
                        {/* This is a workaround for Tooltip hover tracking, Please remove Box and use Tooltip onMouseEnter once Drone fix it */}
                        <Box data-testid="tooltip-hover-span" as="span" onMouseEnter={onTooltipHover}>
                            <Icon id="fill.symbol.circle-info" size="14" pt="4" />
                        </Box>
                    </Tooltip>
                </Box>
                {isMobile ? (
                    <HorizontalScroll data-testid="activities-highlights-horizontal-scroll" overflowY="hidden">
                        {highlightsItems.map((item, index) => (
                            <ActivitiesHighlightsItem key={item.code} cms={cms} highlightsItem={item} index={index} isMobile={isMobile} />
                        ))}
                    </HorizontalScroll>
                ) : (
                    <Box display="flex">
                        {highlightsItems.map((item, index) => (
                            <ActivitiesHighlightsItem key={item.code} cms={cms} highlightsItem={item} index={index} isMobile={isMobile} />
                        ))}
                    </Box>
                )}
            </Card>
        </InView>
    );
};

ActivitiesHighlights.displayName = 'ActivitiesHighlights';

export default connect(mapStateToProps)(ActivitiesHighlights);
